import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { useSilverVersions, useStartValidationRun, useValidationRun } from "utils";

import { POLL_TIMEOUT_MS, RERUN_CONFIRM_DESCRIPTION, RERUN_CONFIRM_TITLE, RERUN_HINT, SOIL_PROPERTY_METRICS } from "consts";

import { ValidationRow, versionOptionLabel } from "models";

import { AlertBanner, Button, Flex, Option, Popconfirm, Select, SelectSegmented, Tabs, TagNormal, Tooltip } from "components";

import { MultipleVersionCharts } from "pages/admin/soilSight/components/multiple/MultipleVersionCharts.tsx";
import { MultipleVersionCommentModal } from "pages/admin/soilSight/components/multiple/MultipleVersionCommentModal.tsx";
import { MultipleVersionScatterGrid } from "pages/admin/soilSight/components/multiple/MultipleVersionScatterGrid.tsx";
import { MultipleVersionSideBySideCharts } from "pages/admin/soilSight/components/multiple/MultipleVersionSideBySideCharts.tsx";
import { MultipleVersionSummary } from "pages/admin/soilSight/components/multiple/MultipleVersionSummary.tsx";
import {
  MultipleVersionLocationRotationTable,
  MultipleVersionLocationTable,
  MultipleVersionTable,
} from "pages/admin/soilSight/components/multiple/MultipleVersionTable.tsx";
import { ErrorBanner } from "pages/admin/soilSight/components/shared/ErrorBanner.tsx";
import { ValidationDetail } from "pages/admin/soilSight/components/shared/ValidationDetail.tsx";
import { ValidationLocations } from "pages/admin/soilSight/components/shared/ValidationLocations.tsx";
import { useOnNavigationSearchParams } from "pages/admin/soilSight/hooks/useOnNavigationSearchParams";
import { computeDeltas, computeLocationDeltas, computeLocationRotationDeltas } from "pages/admin/soilSight/utils/compareUtils.ts";
import { formatRunDuration } from "pages/admin/soilSight/utils/runDuration";
import { readCompareVersions, writeCompareVersions } from "pages/admin/soilSight/utils/slotLocalStorage.ts";

type RunState = { runId: string | null; startedAt: number | null; startError: string | null; starting: boolean; cached: boolean | null };
const EMPTY_RUN_STATE: RunState = { runId: null, startedAt: null, startError: null, starting: false, cached: null };

export const CachedBadge = ({ cached }: { cached: boolean | null }) => {
  if (cached === null) return null;
  return (
    <TagNormal
      text={cached ? "Served from cache" : "Freshly computed"}
      style={{ background: cached ? "#dcfce7" : "#e0f2fe", color: cached ? "#16a34a" : "#0369a1", border: "none" }}
    />
  );
};

export const DurationBadge = ({ startedAt, completedAt }: { startedAt: string | null | undefined; completedAt: string | null | undefined }) => {
  const duration = formatRunDuration(startedAt, completedAt);
  if (!duration) return null;
  return <TagNormal text={`Took ${duration}`} style={{ background: "#f1f5f9", color: "#334155", border: "none" }} />;
};

// Single-version view (Detail / GEE Locations) with a baseline/candidate toggle.
const PerVersionPanel = ({
  baselineVersion,
  candidateVersion,
  baselineRows,
  candidateRows,
  children,
}: {
  baselineVersion: string;
  candidateVersion: string;
  baselineRows: ValidationRow[];
  candidateRows: ValidationRow[];
  children: (rows: ValidationRow[]) => React.ReactNode;
}) => {
  const [side, setSide] = useState<"baseline" | "candidate">("baseline");
  const rows = side === "baseline" ? baselineRows : candidateRows;

  return (
    <Flex vertical gap="0.75rem">
      <SelectSegmented
        options={[
          { label: `${baselineVersion} (baseline)`, value: "baseline" },
          { label: `${candidateVersion} (candidate)`, value: "candidate" },
        ]}
        value={side}
        onChange={(value) => setSide(value as "baseline" | "candidate")}
      />
      {children(rows)}
    </Flex>
  );
};

export const SoilSightMultipleVersionOutcomes = () => {
  const { data: silverVersions } = useSilverVersions();
  const versions = useMemo(() => silverVersions ?? [], [silverVersions]);

  const [baselineVersion, setBaselineVersion] = useState<string | null>(() => readCompareVersions().baseline);
  const [candidateVersion, setCandidateVersion] = useState<string | null>(() => readCompareVersions().candidate);
  const [baselineRun, setBaselineRun] = useState<RunState>(EMPTY_RUN_STATE);
  const [candidateRun, setCandidateRun] = useState<RunState>(EMPTY_RUN_STATE);
  // The versions the currently shown deltas (if any) were actually run for — diverge from
  // baseline/candidateVersion as soon as either dropdown changes, since selecting never auto-runs.
  const [ranForBaseline, setRanForBaseline] = useState<string | null>(null);
  const [ranForCandidate, setRanForCandidate] = useState<string | null>(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  const { data: baselineRunData } = useValidationRun(baselineRun.runId, baselineRun.startedAt);
  const { data: candidateRunData } = useValidationRun(candidateRun.runId, candidateRun.startedAt);

  const baselineOptions = versions.filter((v) => v.version !== candidateVersion);
  const candidateOptions = versions.filter((v) => v.version !== baselineVersion);

  // mutateAsync, not mutate with per-call callbacks: both runs start together and mutate only calls back for the latest.
  const { mutateAsync: startRun } = useStartValidationRun();

  const runOne = useCallback(
    async (versionTag: string, setState: Dispatch<SetStateAction<RunState>>, setRanFor: Dispatch<SetStateAction<string | null>>, forceRerun = false) => {
      setRanFor(versionTag);
      setState({ runId: null, startedAt: null, startError: null, starting: true, cached: null });
      try {
        const { runId, cached } = await startRun({ versionTag, forceRerun });
        setState({ runId, startedAt: Date.now(), startError: null, starting: false, cached });
      } catch {
        setState({ runId: null, startedAt: null, startError: `Failed to start run for ${versionTag}`, starting: false, cached: null });
      }
    },
    [startRun]
  );

  const handleRunComparison = useCallback(() => {
    if (!baselineVersion || !candidateVersion) return;
    runOne(baselineVersion, setBaselineRun, setRanForBaseline);
    runOne(candidateVersion, setCandidateRun, setRanForCandidate);
  }, [baselineVersion, candidateVersion, runOne]);

  const handleRerun = useCallback(() => {
    if (!baselineVersion || !candidateVersion) return;
    runOne(baselineVersion, setBaselineRun, setRanForBaseline, true);
    runOne(candidateVersion, setCandidateRun, setRanForCandidate, true);
  }, [baselineVersion, candidateVersion, runOne]);

  // Remember the last selection so a reload restores the dropdowns (does not auto-run).
  useEffect(() => {
    writeCompareVersions(baselineVersion, candidateVersion);
  }, [baselineVersion, candidateVersion]);

  // Deep-link from the reviews table: preselect both versions (does not auto-run the comparison).
  useOnNavigationSearchParams((params) => {
    const baseline = params.get("baseline");
    const candidate = params.get("candidate");
    if (baseline && candidate) {
      setBaselineVersion(baseline);
      setCandidateVersion(candidate);
    }
  });

  const baselineRows = useMemo(() => (baselineRunData?.status === "completed" ? baselineRunData.result ?? [] : null), [baselineRunData]);
  const candidateRows = useMemo(() => (candidateRunData?.status === "completed" ? candidateRunData.result ?? [] : null), [candidateRunData]);

  const baselineFailedMessage = baselineRunData?.status === "failed" ? baselineRunData.error ?? "Baseline run failed" : null;
  const candidateFailedMessage = candidateRunData?.status === "failed" ? candidateRunData.error ?? "Candidate run failed" : null;
  const selectionOutOfSync = (!!baselineVersion && baselineVersion !== ranForBaseline) || (!!candidateVersion && candidateVersion !== ranForCandidate);

  const [now, setNow] = useState(() => Date.now());
  const baselineTimedOut = !!baselineRun.startedAt && !baselineRows && !baselineFailedMessage && now - baselineRun.startedAt > POLL_TIMEOUT_MS;
  const candidateTimedOut = !!candidateRun.startedAt && !candidateRows && !candidateFailedMessage && now - candidateRun.startedAt > POLL_TIMEOUT_MS;

  const baselineBusy = baselineRun.starting || (!!baselineRun.runId && !baselineRows && !baselineFailedMessage && !baselineTimedOut);
  const candidateBusy = candidateRun.starting || (!!candidateRun.runId && !candidateRows && !candidateFailedMessage && !candidateTimedOut);
  const isRunning = baselineBusy || candidateBusy;

  // Tick only while a run is in flight so the timeout is detected; stops once nothing is busy (including after a timeout).
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, [isRunning]);

  const deltas = baselineRows && candidateRows ? computeDeltas(baselineRows, candidateRows) : null;
  const locationDeltas = baselineRows && candidateRows ? computeLocationDeltas(baselineRows, candidateRows) : null;
  const locationRotationDeltas = baselineRows && candidateRows ? computeLocationRotationDeltas(baselineRows, candidateRows) : null;

  const compareTabs = useMemo(
    () =>
      deltas && baselineRows && candidateRows && locationDeltas && locationRotationDeltas
        ? [
            {
              key: "compare",
              label: "CM and Model outcomes",
              children: (
                <>
                  <MultipleVersionSummary deltas={deltas} />
                  <MultipleVersionTable deltas={deltas} />
                </>
              ),
            },
            { key: "charts", label: "CM and Model outcomes (Delta charts)", children: <MultipleVersionCharts csvA={baselineRows} csvB={candidateRows} /> },
            {
              key: "sidebyside",
              label: "CM and Model outcomes (Side-by-side)",
              children: <MultipleVersionSideBySideCharts csvA={baselineRows} csvB={candidateRows} />,
            },
            { key: "scatter", label: "Scatter", children: <MultipleVersionScatterGrid csvA={baselineRows} csvB={candidateRows} /> },
            {
              key: "soil-table",
              label: "Soil properties",
              children: (
                <Flex vertical gap="1rem">
                  <MultipleVersionLocationTable deltas={locationDeltas} baselineVersion={baselineVersion} candidateVersion={candidateVersion} />
                  <MultipleVersionLocationRotationTable deltas={locationRotationDeltas} baselineVersion={baselineVersion} candidateVersion={candidateVersion} />
                </Flex>
              ),
            },
            {
              key: "sp-side",
              label: "Soil Props (Side-by-side)",
              children: <MultipleVersionSideBySideCharts csvA={baselineRows} csvB={candidateRows} metrics={SOIL_PROPERTY_METRICS} />,
            },
            {
              key: "sp-scatter",
              label: "Soil Props (Scatter)",
              children: <MultipleVersionScatterGrid csvA={baselineRows} csvB={candidateRows} metrics={SOIL_PROPERTY_METRICS} />,
            },
            {
              key: "detail",
              label: "Detail",
              children: (
                <PerVersionPanel
                  baselineVersion={baselineVersion ?? ""}
                  candidateVersion={candidateVersion ?? ""}
                  baselineRows={baselineRows}
                  candidateRows={candidateRows}
                >
                  {(rows) => <ValidationDetail rows={rows} />}
                </PerVersionPanel>
              ),
            },
            {
              key: "gee",
              label: "GEE Locations",
              children: (
                <PerVersionPanel
                  baselineVersion={baselineVersion ?? ""}
                  candidateVersion={candidateVersion ?? ""}
                  baselineRows={baselineRows}
                  candidateRows={candidateRows}
                >
                  {(rows) => <ValidationLocations rows={rows} />}
                </PerVersionPanel>
              ),
            },
          ]
        : [],
    [deltas, baselineRows, candidateRows, locationDeltas, locationRotationDeltas, baselineVersion, candidateVersion]
  );

  return (
    <>
      <div className="soil-sight-validation-toolbar">
        <div className="soil-sight-validation-mode-toggle">
          <Select label="Baseline version" style={{ width: 280 }} value={baselineVersion ?? undefined} onChange={setBaselineVersion}>
            {baselineOptions.map((v) => (
              <Option key={v.version} value={v.version}>
                {versionOptionLabel(v)}
              </Option>
            ))}
          </Select>
          <Select label="Candidate version" style={{ width: 280 }} value={candidateVersion ?? undefined} onChange={setCandidateVersion}>
            {candidateOptions.map((v) => (
              <Option key={v.version} value={v.version}>
                {versionOptionLabel(v)}
              </Option>
            ))}
          </Select>
        </div>
        <Flex gap="1rem">
          <Button type="primary" disabled={!baselineVersion || !candidateVersion || isRunning} loading={isRunning} onClick={handleRunComparison}>
            Run comparison
          </Button>
          <Tooltip title={RERUN_HINT}>
            <Popconfirm color="white" title={RERUN_CONFIRM_TITLE} description={RERUN_CONFIRM_DESCRIPTION} onConfirm={handleRerun} disabled={!deltas}>
              <Button type="primary" disabled={!deltas}>
                Re-run
              </Button>
            </Popconfirm>
          </Tooltip>
          <Button type="primary" onClick={() => setIsReviewOpen(true)} disabled={!deltas}>
            Review this comparison
          </Button>
        </Flex>
        {deltas && (
          <Flex gap="0.5rem">
            <CachedBadge cached={baselineRun.cached} />
            <DurationBadge startedAt={baselineRunData?.startedAt} completedAt={baselineRunData?.completedAt} />
            <CachedBadge cached={candidateRun.cached} />
            <DurationBadge startedAt={candidateRunData?.startedAt} completedAt={candidateRunData?.completedAt} />
          </Flex>
        )}
      </div>

      {baselineRun.startError && <ErrorBanner message={baselineRun.startError} />}
      {candidateRun.startError && <ErrorBanner message={candidateRun.startError} />}
      {baselineFailedMessage && <ErrorBanner message={baselineFailedMessage} />}
      {candidateFailedMessage && <ErrorBanner message={candidateFailedMessage} />}
      {baselineTimedOut && <ErrorBanner message="Baseline run timed out after 10 minutes. Run the comparison again to retry." />}
      {candidateTimedOut && <ErrorBanner message="Candidate run timed out after 10 minutes. Run the comparison again to retry." />}

      {isRunning && <div className="soil-sight-validation-compare-empty-hint">Running scenarios — this can take several minutes on a fresh version.</div>}

      {(!baselineVersion || !candidateVersion) && (
        <div className="soil-sight-validation-compare-empty-hint">Select a baseline and a candidate version, then run the comparison.</div>
      )}

      {baselineVersion && candidateVersion && selectionOutOfSync && !isRunning && (
        <AlertBanner
          type="warning"
          description={
            deltas
              ? `Showing results for ${ranForBaseline} vs ${ranForCandidate}, not the currently selected ${baselineVersion} vs ${candidateVersion}. Click "Run comparison" to refresh.`
              : `${baselineVersion} vs ${candidateVersion} has not been run yet. Click "Run comparison" to see results.`
          }
        />
      )}

      {baselineVersion && candidateVersion && deltas && deltas.length === 0 && <ErrorBanner message="No matching rows found between the two versions." />}

      {deltas && deltas.length > 0 && <Tabs defaultActiveKey="compare" size="small" items={compareTabs} />}

      {baselineVersion && candidateVersion && (
        <MultipleVersionCommentModal
          open={isReviewOpen}
          onClose={() => setIsReviewOpen(false)}
          baselineVersion={baselineVersion}
          candidateVersion={candidateVersion}
        />
      )}
    </>
  );
};
